import {computed, signal} from '@angular/core';
import {BaseFacade} from './base-facade.abstract';
import {Page} from '../models/page-response.model';
import {PageableRequest, PaginationEvent, PaginationSorting} from '../models/pagable.model';
import {AvailablePaginationSize} from '../types/available-pagination-size.type';

export abstract class PageableFacade extends BaseFacade {

  protected currentPageSignal = signal<number>(0);
  protected pageSizeSignal = signal<AvailablePaginationSize>(50);
  protected totalElementsSignal = signal<number>(0);
  protected totalPagesSignal = signal<number>(0);
  protected sortSignal = signal<PaginationSorting | undefined>(undefined);


  readonly currentPage = this.currentPageSignal.asReadonly();
  readonly pageSize = this.pageSizeSignal.asReadonly();
  readonly totalElements = this.totalElementsSignal.asReadonly();
  readonly totalPages = this.totalPagesSignal.asReadonly();
  readonly sort = this.sortSignal.asReadonly();

  readonly hasNextPage = computed(() =>
    this.currentPage() < this.totalPages() - 1
  );

  readonly hasPreviousPage = computed(() => this.currentPage() > 0);
  readonly currentPageDisplay = computed(() => this.currentPage() + 1);
  readonly paginationInfo = computed(() =>
    `Strona ${this.currentPageDisplay()} z ${this.totalPages()} (${this.totalElements()} elementów)`
  );

  abstract loadPage(pageable: PageableRequest, filters?: any): void;

  public onPageChange(event: any): void {
    const pageEvent: PaginationEvent = {...event, sort: this.sort()};
    const pageable = PageableRequest.fromPaginationComponent(pageEvent);
    this.loadPage(pageable);
  }

  public loadFirstPage(filters?: any): void {
    const pageable = new PageableRequest(0, this.pageSize(), this.sort());
    this.loadPage(pageable, filters);
  }

  public loadNextPage(): void {
    if (this.hasNextPage()) {
      const pageable = new PageableRequest(
        this.currentPage() - 1,
        this.pageSize(),
        this.sort()
      );
      this.loadPage(pageable);
    }
  }

  public loadPreviousPage(): void {
    if (this.hasPreviousPage()) {
      const pageable = new PageableRequest(
        this.currentPage() - 1,
        this.pageSize()
      );
      this.loadPage(pageable);
    }
  }

  public refreshCurrentPage(): void {
    const pageable = new PageableRequest(
      this.currentPage(),
      this.pageSize(),
      this.sort()
    );
    this.loadPage(pageable);
  }

  public changePageSize(newSize: AvailablePaginationSize): void {
    this.pageSizeSignal.set(newSize);
    this.loadFirstPage();
  }

  public setSort(sort: PaginationSorting | undefined): void {
    this.sortSignal.set(sort);
  }

  public resetPagination(): void {
    this.currentPageSignal.set(0);
    this.totalElementsSignal.set(0);
    this.totalPagesSignal.set(0);
  }

  public updatePaginationFromPage<T>(pageData: Page<T>): void {
    this.currentPageSignal.set(pageData.number);
    this.pageSizeSignal.set(pageData.size);
    this.totalElementsSignal.set(pageData.totalElements);
    this.totalPagesSignal.set(pageData.totalPages);
  }
}
